import AppNavigationBar from '@rio-cloud/rio-uikit/AppNavigationBar';
import ExpanderList from '@rio-cloud/rio-uikit/ExpanderList';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import { useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { KeyValue } from '#components/shared/KeyValue.tsx';
import { TimeTooltip } from '#components/shared/TimeTooltip.tsx';
import { getServiceOrderExpanderListItem } from '#components/tourstopdetails/ServiceOrderExpanderListItem.tsx';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import type { ServiceOrder } from '#domain/model/ServiceOrder.ts';
import type { Stop, StopType } from '#domain/model/Stop.ts';
import type { TransportOrder } from '#domain/model/TransportOrder.ts';
import { getTransportOrderExpanderListItem } from '#features/tours/sidebar/TransportOrderExpanderListItem.tsx';
import { useTrackScreenSeen } from '#hooks/useTrackScreenSeen.ts';
import { TimeDifference } from '#utils/timeDifference.tsx';

type ContainerType = 'widget' | 'sidebar';

type TourStopDetailsProps = {
    title: string;
    stop: Stop;
    transportOrders: TransportOrder[];
    serviceOrders: ServiceOrder[];
    onBack: () => void;
    type: ContainerType;
};

const getExpanderListItems = ({
    stopType,
    transportOrders,
    serviceOrders,
    setSelectedItemIndex,
    selectedItemIndex,
}: {
    stopType: StopType;
    transportOrders: TransportOrder[];
    serviceOrders: ServiceOrder[];
    setSelectedItemIndex: (stopId?: number) => void;
    selectedItemIndex?: number;
}) => {
    switch (stopType) {
        case 'load':
        case 'unload':
        case 'unknown':
            return transportOrders.map((order, index) =>
                getTransportOrderExpanderListItem({
                    stopType: stopType,
                    transportOrder: order,
                    index: index,
                    isSelected: index === selectedItemIndex,
                    setSelectedIndex: setSelectedItemIndex,
                })
            );
        case 'service':
            return serviceOrders.map((order, index) =>
                getServiceOrderExpanderListItem(order, index, index === selectedItemIndex, setSelectedItemIndex)
            );
    }
};

export const TourStopDetails = ({
    title,
    stop,
    transportOrders,
    serviceOrders,
    onBack,
    type,
}: TourStopDetailsProps) => {
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>(0);
    const intl = useIntl();
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);

    useTrackScreenSeen(onScreen, {
        category: TRACKING_CATEGORIES.LIVEMONITOR,
        action: TRACKING_ACTIONS.ON_SCREEN,
        label: 'Show tour stop details view',
    });

    const expanderListItems = getExpanderListItems({
        stopType: stop.type,
        transportOrders: transportOrders,
        serviceOrders: serviceOrders,
        setSelectedItemIndex: setSelectedItemIndex,
        selectedItemIndex: selectedItemIndex,
    });

    return (
        <>
            <AppNavigationBar
                title={title}
                onNavigateBack={onBack}
                className={`border ${type === 'widget' ? 'border-left-none border-right-none' : 'border-bottom-only'}`}
            />
            <div className={`${type === 'widget' ? 'padding-x-15' : ''}`} ref={ref}>
                <div className={`padding-15 bg-lightest ${type === 'widget' ? 'margin-y-20 border rounded' : ''}`}>
                    <div className='text-medium margin-bottom-5'>
                        {stop.location.country} - {stop.location.zipCode} {stop.location.city}, {stop.location.street}
                    </div>
                    <KeyValue
                        keyData={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.activity',
                        })}
                        valueData={
                            <FormattedMessage id={`intl-msg:ordercom-cloud-frontend.tours.stop.type.${stop.type}`} />
                        }
                    />
                    <KeyValue
                        keyData={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.arrivalTime',
                        })}
                        valueData={
                            <TimeTooltip value={stop.startTarget}>
                                <div className='flex-1-1-0 text-medium'>
                                    <FormattedDate
                                        value={stop.startActual ?? stop.startTarget}
                                        timeStyle={'short'}
                                        dateStyle={'short'}
                                    />
                                    <TimeDifference actual={stop.startActual} expected={stop.startTarget} />
                                </div>
                            </TimeTooltip>
                        }
                    />
                    <KeyValue
                        keyData={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.stop-details.departureTime',
                        })}
                        valueData={
                            <TimeTooltip value={stop.endTarget}>
                                <div className='flex-1-1-0 text-medium'>
                                    <FormattedDate
                                        value={stop.endActual ?? stop.endTarget}
                                        timeStyle={'short'}
                                        dateStyle={'short'}
                                    />
                                    <TimeDifference actual={stop.endActual} expected={stop.endTarget} />
                                </div>
                            </TimeTooltip>
                        }
                    />
                </div>
                {type === 'widget' && expanderListItems.length === 1 ? (
                    <div className={'display-flex flex-column gap-5'}>
                        <span className='text-medium'>{expanderListItems[0].header}</span>
                        {expanderListItems[0].body}
                    </div>
                ) : (
                    <ExpanderList
                        items={expanderListItems}
                        className={type === 'sidebar' ? 'border border-left-none border-right-none rounded-none' : ''}
                    />
                )}
            </div>
        </>
    );
};
